import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChatCircleDots, PaperPlaneTilt, X, Robot } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genSessionId = () => {
  const existing = localStorage.getItem("ap_chat_session");
  if (existing) return existing;
  const id = `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  localStorage.setItem("ap_chat_session", id);
  return id;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hoi! Ik ben de digitale assistent van Rijschool AutoPilot. Vraag me alles over pakketten, proefles of onze aanpak — ik help je graag!",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(genSessionId);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.data.response },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Oeps, er ging iets mis. Probeer het zo opnieuw of stuur ons direct een WhatsApp bericht op 06-20933773.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 left-6 z-30 w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform"
        aria-label="Open chat"
        data-testid="chat-launcher-btn"
      >
        {open ? (
          <X size={24} weight="bold" color="#070a13" />
        ) : (
          <ChatCircleDots size={26} weight="fill" color="#070a13" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="z-30 w-[92vw] sm:w-[380px] h-[540px] max-h-[80vh] card-highlight flex flex-col overflow-hidden"
            style={{ position: "fixed", bottom: "6rem", left: "1.5rem" }}
            data-testid="chat-widget"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                <Robot size={20} weight="duotone" className="text-gold" />
              </div>
              <div className="flex-1">
                <div className="text-white text-sm font-semibold">
                  AutoPilot Assistent
                </div>
                <div className="text-xs text-silver flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                  Online
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
              data-testid="chat-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "chat-bubble-user rounded-[16px_16px_4px_16px]"
                        : "chat-bubble-bot rounded-[16px_16px_16px_4px]"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="chat-bubble-bot rounded-[16px_16px_16px_4px] px-4 py-3 flex gap-1">
                    <span className="typing-dot w-1.5 h-1.5 bg-gold rounded-full inline-block"></span>
                    <span className="typing-dot w-1.5 h-1.5 bg-gold rounded-full inline-block"></span>
                    <span className="typing-dot w-1.5 h-1.5 bg-gold rounded-full inline-block"></span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={send}
              className="p-3 border-t border-white/10 flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Stel een vraag..."
                className="field flex-1 text-sm"
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="btn-gold px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="chat-send-btn"
                aria-label="Verstuur"
              >
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
